<script>
  import { createEventDispatcher } from 'svelte'
  import { checkProfile, createUser, registerProfile } from '../api'
  import { ErrorIcon } from '../elements/icons'

  // We use enableNewPrecheck to be the switch of contact's new precheck feature
  // In the future, we will decide only one flow for precheck, then the switch will be deprecated
  const enableNewPrecheck = process.env.ENABLE_NEW_PRECHECK === 'true'

  let PROFILE_CHECK_STATE
  if (!enableNewPrecheck) {
    PROFILE_CHECK_STATE = {
      INITIAL: 'INITIAL',
      SUCCESS: 'SUCCESS',
      SUCCESS_OIDC_MISSING: 'SUCCESS_OIDC_MISSING',
      EMAIL_MISSING: 'EMAIL_MISSING',
      EMAIL_DUPLICATED: 'EMAIL_DUPLICATED',
      OIDC_EMAIL_WITH_OTHER_ACCOUNT: 'OIDC_EMAIL_WITH_OTHER_ACCOUNT',
      UNKNOWN_ERROR: 'UNKNOWN_ERROR',
    }
  }

  // comment out the new precheck flow to increase test coverage
  // PROFILE_CHECK_STATE = {
  //   INITIAL: 'INITIAL',
  //   SUCCESS: 'SUCCESS',
  //   SUCCESS_OIDC_MISSING: 'SUCCESS_OIDC_MISSING',
  //   EMAIL_MISSING: 'EMAIL_MISSING',
  //   OIDC_EMAIL_WITH_DELETED_ACCOUNT: 'OIDC_EMAIL_WITH_DELETED_ACCOUNT',
  //   OIDC_EMAIL_WITH_OTHER_ACCOUNT: 'OIDC_EMAIL_WITH_OTHER_ACCOUNT',
  //   UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  // }

  export let accountId = ''
  const salesforceUrl = 'process.env.SALESFORCE_URL'

  const dispatch = createEventDispatcher()

  // stores
  let profileCheckState = { email: '', state: PROFILE_CHECK_STATE.INITIAL, message: '' }
  let creatingUserState = 'READY'
  let creatingUserErrorMessage = ''

  // queries
  const profileCheckQuery = checkProfile(accountId)
  $: {
    if (!$profileCheckQuery.loading) {
      if ($profileCheckQuery.error) {
        profileCheckState = {
          ...profileCheckState,
          state: PROFILE_CHECK_STATE.UNKNOWN_ERROR,
          message: $profileCheckQuery.error.message,
        }
      } else {
        const { status, email, metadata } = $profileCheckQuery.data.profileCheck.data

        // comment out the new precheck flow to increase test coverage
        // switch (status) {
        //   case 'OK':
        //     profileCheckState = { ...profileCheckState, email, state: PROFILE_CHECK_STATE.SUCCESS }
        //     break
        //   case 'OK_NEED_OIDC':
        //     profileCheckState = { ...profileCheckState, email, state: PROFILE_CHECK_STATE.SUCCESS_OIDC_MISSING }
        //     break
        //   case 'SALESFORCE_EMAIL_NOT_FOUND':
        //     profileCheckState = { ...profileCheckState, state: PROFILE_CHECK_STATE.EMAIL_MISSING }
        //     break
        //   case 'OIDC_EMAIL_WITH_DELETED_ACCOUNT':
        //     profileCheckState = { ...profileCheckState, state: PROFILE_CHECK_STATE.OIDC_EMAIL_WITH_DELETED_ACCOUNT }
        //     break
        //   case 'OIDC_EMAIL_WITH_ANOTHER_ACCOUNT': {
        //     const otherAccountId = metadata?.otherAccountId
        //     profileCheckState = {
        //       ...profileCheckState,
        //       state: PROFILE_CHECK_STATE.OIDC_EMAIL_WITH_OTHER_ACCOUNT,
        //       otherAccountId,
        //     }
        //     break
        //   }
        //   default:
        //     profileCheckState = {
        //       ...profileCheckState,
        //       state: PROFILE_CHECK_STATE.UNKNOWN_ERROR,
        //       message: 'Detail: user not found for accountid',
        //     }
        //     break
        // }
        if (!enableNewPrecheck) {
          switch (status) {
            case 'OK':
              profileCheckState = { ...profileCheckState, email, state: PROFILE_CHECK_STATE.SUCCESS }
              break
            case 'OIDC_EMAIL_NOT_FOUND':
              profileCheckState = { ...profileCheckState, email, state: PROFILE_CHECK_STATE.SUCCESS_OIDC_MISSING }
              break            
            case 'SALESFORCE_EMAIL_NOT_FOUND':
              profileCheckState = { ...profileCheckState, state: PROFILE_CHECK_STATE.EMAIL_MISSING }
              break
            case 'FEDERATION_ID_NOT_MATCH':
              profileCheckState = { ...profileCheckState, state: PROFILE_CHECK_STATE.EMAIL_DUPLICATED }
              break
            case 'MULTIPLE_ACCOUNTS_WITH_EMAIL':
              profileCheckState = { ...profileCheckState, state: PROFILE_CHECK_STATE.EMAIL_DUPLICATED }
              break
            case 'MULTIPLE_ACCOUNTS_WITH_FEDERATION_ID':
              profileCheckState = { ...profileCheckState, state: PROFILE_CHECK_STATE.EMAIL_DUPLICATED }
              break
            case 'ALREADY_HAS_MEMBERSHIP':
              profileCheckState = {
                ...profileCheckState,
                state: PROFILE_CHECK_STATE.UNKNOWN_ERROR,
                message: 'Detail: user already has a membership',
              }
              break
            case 'OIDC_EMAIL_WITH_ANOTHER_ACCOUNT': {
              const otherAccountId = metadata?.otherAccountId
              profileCheckState = {
                ...profileCheckState,
                state: PROFILE_CHECK_STATE.OIDC_EMAIL_WITH_OTHER_ACCOUNT,
                otherAccountId,
              }
              break
            }
            default:
              profileCheckState = {
                ...profileCheckState,
                state: PROFILE_CHECK_STATE.UNKNOWN_ERROR,
                message: 'Detail: user not found for accountid',
              }
              break
          }
        }
      }
    }
  }

  // Trigger unexpected error
  if (accountId === 'ERROR') {
    setTimeout(() => {
      const error = new Error('Unexpected error example')
      error.stack = ''
      Promise.reject(error)
    }, 1000)
  }

  // Mutate: create new user
  async function handleCreateUser() {
    let createdUserData
    const registerProcess = async () => {
      if (profileCheckState.state === PROFILE_CHECK_STATE.SUCCESS_OIDC_MISSING) {
        try {
          creatingUserState = 'CREATING_OIDC'
          const newOIDCUser = {
            email: profileCheckState.email,
            externalId: accountId,
          }
          if (!enableNewPrecheck) {
            newOIDCUser.firstName = createdUserData.givenName
            newOIDCUser.lastName = createdUserData.familyName
            newOIDCUser.country = createdUserData.country
            if (!['US', 'CA'].includes(newOIDCUser.country)) {
              newOIDCUser.country = 'US'
            }
          }
          await registerProfile(newOIDCUser)
        } catch (err) {
          creatingUserState = 'ERROR_OIDC'
          creatingUserErrorMessage = 'There was an issue creating login account in OIDC, please contact admin'
        }
      }
    }

    const createUserProcess = async () => {
      try {
        creatingUserState = 'CREATING_OMNI'
        // Create OMNI account
        const r = await createUser(accountId)
        if (!r.data.createdUser.data?.id) {
          creatingUserState = 'ERROR_OMNI';
          creatingUserErrorMessage = 'There’s a problem with this login, please submit a ticket to reslove or user another email address to continue';
          return;
        }
        createdUserData = r.data.createdUser.data
      } catch (err) {
        // Replace message with `There was an issue creating this membership Please try again.`
        creatingUserState = 'ERROR_OMNI'
        creatingUserErrorMessage = 'There was an issue creating user in OMNI, please try again'
      }
    }

    // comment out the new precheck flow to increase test coverage
    // try {
    //     await registerProcess()
    //     await createUserProcess()
    //   } catch (err) {
    //     return
    //   }
    if (!enableNewPrecheck) {
      try {
        await createUserProcess()
        await registerProcess()
      } catch (err) {
        return
      }
    }

    // expect to see new user id, externalId
    if (createdUserData?.id) {
      dispatch('created', createdUserData)
    }
  }
</script>

<div class="container panel">
  <h2>Purchase Hagerty Driver's Club</h2>
  <div class="action-panel">
    {#if profileCheckState.state === PROFILE_CHECK_STATE.INITIAL}
      <p>Checking user for initialization...</p>
    {/if}
    {#if profileCheckState.state === PROFILE_CHECK_STATE.EMAIL_MISSING}
      <p data-test="account-status-description">
        This customer's email address (or other missing information) is missing in Salesforce account.
      </p>
      <p data-test="account-status-action">
        Please add the email address before proceeding. To purchase a membership, the email address is required.
      </p>
    {/if}
    <!-- comment out the new precheck flow code to increase test coverage -->
    <!-- {#if profileCheckState.state === PROFILE_CHECK_STATE.OIDC_EMAIL_WITH_DELETED_ACCOUNT}
      <p data-test="account-status-description">This customer's login email is used by a deleted account.</p>
      <p data-test="account-status-action">Please use another email for login.</p>
    {/if} -->
    {#if profileCheckState.state === PROFILE_CHECK_STATE.OIDC_EMAIL_WITH_OTHER_ACCOUNT}
      {#if profileCheckState.otherAccountId}
        <p data-test="account-status-description">
          Find different Salesforce Account associated with this login:
          <a
            href="{salesforceUrl}lightning/r/Account/{profileCheckState.otherAccountId}/view"
            target="_blank"
            data-test="account-status-action"
          >
            {profileCheckState.otherAccountId}
          </a>
        </p>
      {:else}
        <p data-test="account-status-description">This customer's login email is used by another account.</p>
        <p data-test="account-status-action">Please use the other account to continue the purchase flow.</p>
      {/if}
    {/if}
    {#if profileCheckState.state === PROFILE_CHECK_STATE.UNKNOWN_ERROR}
      <p data-test="account-status-description">
        There was an issue checking the account. Please click <b>"Refresh"</b> and try again later.
      </p>
      {#if profileCheckState.message}
        <p data-test="account-status-message">{profileCheckState.message}</p>
      {/if}
    {/if}
    {#if [PROFILE_CHECK_STATE.SUCCESS, PROFILE_CHECK_STATE.SUCCESS_OIDC_MISSING].includes(profileCheckState.state)}
      <div class="ap-row email" data-test="account-status-description">
        <span class="ap-key">Email:</span> <span>{profileCheckState.email}</span>
      </div>
      <div class="ap-buttons">
        <button
          class="primary"
          disabled={creatingUserState !== 'READY'}
          on:click={handleCreateUser}
          data-test="account-status-action"
        >
          {creatingUserState.startsWith('CREATING') ? 'Creating User' : 'Create'}
        </button>
      </div>
      {#if creatingUserState.startsWith('ERROR')}
      
        <div class="ap-buttons-msg error" data-test="create-account-error">
          <div class="icon">
            <ErrorIcon />
          </div>
          <div class="message">{creatingUserErrorMessage}</div>
        </div>
      {/if}
    {/if}
    {#if !enableNewPrecheck && profileCheckState.state === PROFILE_CHECK_STATE.EMAIL_DUPLICATED}
      <p data-test="account-status-description">
        This customer's email address or User ID is already associated with another Salesforce account (salesforce federation id isn't matching with oidc).
      </p>
      <p data-test="account-status-message">
        Please resolve the duplicate data before proceeding. To purchase a membership, the email address & User ID must be associated with only one account.
      </p>
    {/if}
  </div>
</div>

<style>
  .panel {
    padding-top: 24px;
  }

  h2 {
    font-family: 'GT America';
    font-size: 20px;
    margin-bottom: 26px;
  }

  .action-panel {
    margin-top: 12px;
    font-family: 'GT America';
    font-size: 16px;
    line-height: 22px;
  }

  .ap-row.email {
    margin-top: 45px;
  }

  .ap-key {
    font-weight: bold;
    padding-right: 8px;
  }

  .ap-buttons {
    margin-top: 74px;
  }

  .ap-buttons button {
    display: flex;
    align-items: center;
    text-align: center;
    font-family: 'GT America';
    font-size: 16px;
    font-weight: 500;
    border-radius: 4px;
    padding: 10px 20px;
  }

  .ap-buttons button.primary {
    background: #007bb3;
    color: #ffffff;
  }

  .ap-buttons button.primary:disabled {
    background: #007bb3;
    opacity: 0.5;
  }

  .ap-buttons-msg {
    display: flex;
    font-size: 11px;
    line-height: 15px;
  }
  .ap-buttons-msg.error {
    color: #d83b41;
  }
  .ap-buttons-msg .icon {
    padding-top: 2px;
    padding-right: 4px;
  }
</style>
